import { useEffect, useRef, useState, FunctionComponent } from 'react'
import { get } from 'libraries/lodash'
import cx from 'classnames'
import ArrowForward from '@material-ui/icons/ArrowForward'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import IconButton from '@material-ui/core/IconButton'
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess'
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore'
import { updateHistoryState } from 'front-end-core/core/history'
import { scrollTo } from 'front-end-core/helpers/document'
import TealToolTip from 'components/TealToolTip'
import Selector from 'components/accordion/Selector'
import { AccordionHeaderProps, toDataProps } from 'components/accordion/AccordionHeader'
import { CURRENT_AWARENESS, LEGISLATION } from 'constants/controllers'
import HoverIconButtonContainer from 'redux/containers/components/accordion/HoverIconButtonContainer'
import { GroupHeaderContainerProps } from 'redux/containers/components/accordion/GroupHeaderContainer'
import { GroupProps } from './Group'
import { AccordionGroup } from 'interfaces/accordion'

const GroupHeader: FunctionComponent<GroupHeaderProps> = ({
  atBottom,
  className,
  depth,
  eventCategory,
  expanded,
  id,
  nodeId,
  selected,
  originalNode,
  prefix = '',
  suffix = '',
  isArchived,
  group,
  allContentExpanded,
  setAllContentExpanded,
  cxNames = {},
  showAllS1,
  editMode,
  AdminButton,
  TitleEditor,
  flavour,
  title = '',
  parentTitle,
  urlNode,
  setAccordionUrlNode,
  controller,
  isMobile,
  pageId
}) => {
  const [showEditor, setShowEditor] = useState(false)
  const depthStyle = get(cxNames, 'groupHeader.depthStyle', () => null)
  const iconDepthStyle = get(cxNames, 'groupHeader.iconDepthStyle', () => null)
  const [isUrlOwned, setIsUrlOwned] = useState(false)
  const scrollToRef = useRef()

  const Admin = (editMode && !showEditor && (
    <span className='group-header__admin-button'>
      <AdminButton
        url={null}
        editMode={false}
        onClick={() => setShowEditor(true)}
      />
    </span>
  )) || null

  const Prefix = (prefix && <div className='ttc nowrap mr6'>{prefix}</div>) || null
  const Suffix = (suffix && <span className='fw5 nowrap'>{suffix}</span>) || null
  const childProps = Object.assign(
    { className },
    toDataProps({
      depth,
      expanded: +expanded,
      id,
      nodeId,
      prefix,
      selected,
      suffix,
      title,
      type: 'group',
      isArchived
    })
  )

  const labelPaddingStyle = (depth: number) => depth > 1 ? `0rem 1rem 0rem ${depth}.75rem` : '0.75rem 1rem'

  const showToggleExpandAllIcon = (expanded && depth > 1) && Array.isArray(group)
    ? (group as AccordionGroup[]).filter((node) => node.nodeType === 'content').length > 0
    : false

  const noPointerEvents = (controller === LEGISLATION && depth === 2)

  const handleOnClick = () => {
    if (nodeId && !noPointerEvents) {
      const url = nodeId
      if (controller !== CURRENT_AWARENESS) {
        setAccordionUrlNode(url)
        history.pushState({}, null, url)
        updateHistoryState({ viewName: controller })
      }
    }
  }

  useEffect(() => {
    if (isUrlOwned && expanded) {
      scrollTo({ pageId, element: scrollToRef.current, animate: true, useWindowScrollTo: isMobile })
    }
  }, [isUrlOwned, expanded, scrollToRef])

  useEffect(() => {
    if (controller !== CURRENT_AWARENESS) {
      setIsUrlOwned(nodeId === urlNode)
    }
  }, [controller, nodeId, urlNode, setIsUrlOwned])

  return (
    <div
      ref={scrollToRef}
      {...childProps}
      className={cx({ 'no-pointer-events': noPointerEvents })}
    >
      {showToggleExpandAllIcon && (
        <ExpandAllContentIconButton
          {...{ allContentExpanded, setAllContentExpanded, depth, parentTitle }}
        />
      )}
      <input
        type='checkbox'
        name={id}
        id={id + '/' + atBottom}
        checked={expanded as any}
        data-action='expand'
        data-path={id}
        data-type='group'
        data-value={+!expanded}
        readOnly
        onClick={handleOnClick}
      />
      <label
        style={{
          padding: labelPaddingStyle(depth),
          letterSpacing: '0.1px',
          boxShadow: 'none'
        }}
        className={cx(
          'w-100 f5 b min-h3 flex items-center ma0 bb',
          { 'flex-wrap': isMobile },
          { 'darker ': expanded && depth === 2 },
          {
            [depthStyle(!title && parentTitle ? depth - 1 : depth)]: true,
            group: true,
            'group-header-archived': isArchived
          }
        )}
        htmlFor={id + '/' + atBottom}
        data-event-category={eventCategory}
        data-event-action={expanded ? 'Collapse' : 'Expand'}
        data-event-label={`[Group ${depth}] ${(title || '').replace(
          /(<([^>]+)>)/gi,
          ''
        )}`}
      >
        {showEditor ? null : Prefix}
        {Admin}
        <div
          className='flex items-center justify-center justify-between h-100 w-100'
        >
          {showEditor
            ? (
              <TitleEditor
                value={originalNode.title}
                hide={() => setShowEditor(false)}
                {...{ originalNode }}
              />
              )
            : (
              <div className='flex items-center'>
                <span
                  className={cx(
                    'lh-title mr6',
                    { mv4: depth > 1 },
                    { underline: isUrlOwned }
                  )}
                >
                  {title || parentTitle || ''}
                </span>
                <HoverIconButtonContainer {...{ nodeId }} />
              </div>
              )}
          <div className='flex items-center justify-center'>
            {Suffix}
            {!editMode &&
              <TealToolTip title={selected ? 'Deselect All' : 'Select All'} placement='top-end'>
                <div>
                  <Selector type='group' {...{ atBottom, id, selected, title, flavour, expanded: expanded as any }} />
                </div>
              </TealToolTip>}
            {!expanded && depth === 1 && !showAllS1 && (
              <div className={cx('flex items-center', iconDepthStyle(depth))}>
                <ArrowForward />
              </div>
            )}
            {!expanded && (depth > 1 || showAllS1) && (
              <div className={cx('flex items-center', iconDepthStyle(depth))}>
                <ExpandMoreIcon style={{ width: 24, height: 24 }} />
              </div>
            )}
            {expanded && (depth > 1 || showAllS1) && (
              <div className={cx('flex items-center', iconDepthStyle(depth))}>
                <ExpandLessIcon style={{ width: 24, height: 24 }} />
              </div>
            )}
          </div>
        </div>
        {isArchived && <span className='archived-label'>Archived</span>}
      </label>
    </div>
  )
}

export default GroupHeader

const ExpandAllContentIconButton: FunctionComponent<Partial<GroupHeaderProps>> = ({ allContentExpanded, setAllContentExpanded, depth, parentTitle }) => {
  const handleOnClick = () => setAllContentExpanded(!allContentExpanded)
  const leftOffset = 0.5 + (depth - 2)

  return (
    <div style={{ paddingLeft: `${depth}rem` }}>
      <TealToolTip title={allContentExpanded ? 'Collapse All Answers' : 'Expand All Answers'} placement='top-start' leaveDelay={300}>
        <IconButton
          size='small'
          className={cx('outline-0 absolute top4', { 'cadet-blue': !parentTitle }, { black: parentTitle })}
          style={{ left: `${leftOffset}rem` }}
          onClick={handleOnClick}
        >
          {allContentExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
        </IconButton>
      </TealToolTip>
    </div>
  )
}

type GroupHeaderPropTypes = GroupHeaderContainerProps & Partial<GroupProps> & AccordionHeaderProps

interface GroupHeaderProps extends GroupHeaderPropTypes{
  eventCategory: string
  nodeId: string
  originalNode?: {
    id: string
    title: string
  }
  prefix: string
  suffix: string
  isArchived?: boolean
  allContentExpanded?: boolean
  setAllContentExpanded?: (value: boolean) => void
  cxNames: any
  title?: string
  parentTitle?: string
  display?: number
  group?: AccordionGroup[]
}
